import { db } from '@/models/db';
import type { DeltaPacketRecord } from '@/models/hydrate';
import { hydrateFromWorker } from '@/models/hydrate-worker-utils';
import { SMSAgentMessage } from '@/models/registry';
import type { BaseStore } from '@/models/stores/base';
import type { SMSAgentMessage as SMSAgentMessageType } from '@/models/types';
import { makeAutoObservable } from 'mobx';

class SMSAgentMessagesStore
  implements BaseStore<SMSAgentMessage, SMSAgentMessageType>
{
  store: Map<string, SMSAgentMessage> = new Map();

  storeIndexedByForeignKey: Map<string, SMSAgentMessage[]> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  private setSMSField = <K extends keyof SMSAgentMessageType>(
    message: SMSAgentMessage,
    key: K,
    value: SMSAgentMessageType[K],
  ) => {
    (message as Pick<SMSAgentMessage, K>)[key] = value as SMSAgentMessage[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<SMSAgentMessageType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.sms_agent_messages.delete(delta.record_id);
      }
      this.store.delete(delta.data.id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.sms_agent_messages.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.sms_agent_messages.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.sms_agent_messages.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<SMSAgentMessageType>;
  }) {
    const existingMessage = this.getById(delta.record_id);
    if (!existingMessage) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof SMSAgentMessageType,
      SMSAgentMessageType[keyof SMSAgentMessageType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingMessage[key] === newValue) {
        continue;
      }

      this.setSMSField(existingMessage, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingMessage);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<SMSAgentMessageType>[];
    hydrateIndexedDB?: boolean;
  }) {
    deltas.forEach((delta) => {
      this.applyDelta({ delta, hydrateIndexedDB });
    });
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<SMSAgentMessageType>;
    hydrateIndexedDB?: boolean;
  }) {
    const { action, data } = delta;

    if (hydrateIndexedDB) {
      db.sms_agent_messages.put(data, data.id);
    }

    if (action === 'delete') {
      this.store.delete(data.id);
    }

    this.upsert(data);
  }

  upsert(data: SMSAgentMessageType) {
    const existing = this.store.get(data.id);
    if (existing) {
      this.replace(data);
    } else {
      this.add(data);
    }
  }

  replace(data: SMSAgentMessageType) {
    const existing = this.store.get(data.id);
    if (!existing) return;
    existing.message_id = data.message_id;
    existing.message_date = data.message_date;
    existing.direction = data.direction;
    existing.to_number = data.to_number;
    existing.from_number = data.from_number;
    existing.text = data.text;
    existing.mms = data.mms;
    existing.mms_url = data.mms_url;
  }

  add(data: SMSAgentMessageType) {
    const smsAgentMessage = new SMSAgentMessage(data);
    this.store.set(smsAgentMessage.id, smsAgentMessage);
    // Index by foreign key for O(1) lookups
    const arr =
      this.storeIndexedByForeignKey.get(smsAgentMessage.deal_id) || [];
    arr.push(smsAgentMessage);
    this.storeIndexedByForeignKey.set(smsAgentMessage.deal_id, arr);
  }

  getById(id: string): SMSAgentMessage | undefined {
    return this.store.get(id);
  }

  getByForeignKey(foreignKey: string): SMSAgentMessage[] {
    return this.storeIndexedByForeignKey.get(foreignKey) || [];
  }

  save(smsAgentMessage: SMSAgentMessage) {
    this.store.set(smsAgentMessage.id, smsAgentMessage);
    // update the message in the storeIndexedByForeignKey
    const arr =
      this.storeIndexedByForeignKey.get(smsAgentMessage.deal_id) || [];
    const index = arr.findIndex((m) => m.id === smsAgentMessage.id);
    if (index !== -1) {
      arr[index] = smsAgentMessage;
    }
    this.storeIndexedByForeignKey.set(smsAgentMessage.deal_id, arr);
  }

  async hydrateLocal(): Promise<void> {
    await hydrateFromWorker({
      tableName: 'sms_agent_messages',
      entity: 'sms_agent_messages',
      modelStore: smsAgentMessageStore,
    });
  }
}

export const smsAgentMessageStore = new SMSAgentMessagesStore();
